/***
 *
 *   CLIENT ONBOARDING V2 — SHARED RULES
 *
 *   The vocabulary, the parse and the party classification for the person-first
 *   onboarding form. One module, two callers (KTD5):
 *
 *     - the browser view, which runs it before the POST to block completion
 *     - the server (helper/clientOnboardingV2.js), which runs it as the authority
 *
 *   CommonJS with NO requires, so the browser can bundle it and the server can
 *   require it, the same way client/src/views/verification/australian-identifiers.js
 *   is shared today. Pure: no I/O, no clock, no randomness, no environment.
 *
 *   Lifted from prototypes/client-onboarding-v2/preview/ (vocabulary,
 *   parseSubmission, classifyParties). What changed on the way in:
 *
 *     - The person panel uses the v1 individual names (firstName, lastName, ...;
 *       KTD18), and the nested panel is role_holders (KTD16).
 *     - Entity types are the nine v1 organisation types. "individual" is an error:
 *       a sole trader is a client person.
 *     - Roles are the prototype's ten plus executor.
 *     - A role row the prototype skipped (blank holder, unknown holder, self
 *       reference, blank role) is an error here. v2 never drops a role (R12), and
 *       a row the form should have refused is one the client must fix.
 *     - A shareholder needs an ownership %.
 *     - The CEO/SMO pick is a group field (KTD22). A person with the answer No and
 *       no role who is the pick is an excluded party (ceo_only), not an error.
 *     - Staff can exclude any party before approval (R21). Excluding an entity
 *       moves each of its contact persons to another held entity, or excludes
 *       them (parent_excluded).
 *
 **********/

// The nine v1 organisation types, value for value (bin/onboarding-templates.js
// MULTI_ENTITY_CHOICES minus "individual").
const ENTITY_TYPES = [
  { value: "trust", text: "Trust" },
  { value: "unit_trust", text: "Unit Trust" },
  { value: "smsf", text: "Self-Managed Superannuation Fund (SMSF)" },
  { value: "body_corporate", text: "Company" },
  { value: "not_for_profit", text: "Not for Profit (Charity)" },
  { value: "partnership", text: "Partnership" },
  { value: "unincorporated_association", text: "Unincorporated Association" },
  { value: "government_body", text: "Government Body" },
  { value: "deceased_estate", text: "Deceased Estate" },
];

// The field whose value names the entity. Mirrors the entity-name switch in
// clientGroupOnboardingHandler (controller/formController.js), so a v2 row and a
// v1 row of the same type carry the same name. SMSF differs: v2 asks an SMSF its
// own questions (smsf_panel), where v1 uses the trust panel.
const ENTITY_NAME_FIELD = {
  trust: "trust_name",
  unit_trust: "trust_name",
  smsf: "smsf_name",
  body_corporate: "company_name",
  not_for_profit: "company_name",
  partnership: "partnership_name",
  unincorporated_association: "association_name",
  government_body: "gov_entity_name",
  deceased_estate: "estate_name",
};

// The roles a role row may carry: the prototype's ten (the same list v1 offers
// on its relationships page) plus executor. helper/xpmClientGroups.js maps every
// one of them to an XPM relationship type.
const ROLES = [
  { value: "trustee", text: "Trustee" },
  { value: "director", text: "Director" },
  { value: "shareholder", text: "Shareholder" },
  { value: "partner", text: "Partner" },
  { value: "beneficial_owner", text: "Beneficial owner" },
  { value: "appointor", text: "Appointor" },
  { value: "settlor", text: "Settlor" },
  { value: "protector", text: "Protector / Guardian" },
  { value: "office_holder", text: "Office holder" },
  { value: "executor", text: "Executor / Administrator" },
  { value: "other", text: "Other" },
];

// Roles that carry an ownership figure on the row. The figure stays in
// IdentityCheck; the XPM relationship sync never sends it.
const OWNERSHIP_ROLES = ["shareholder", "beneficial_owner", "partner"];

// R11: a shareholding must say how much.
const OWNERSHIP_REQUIRED_ROLES = ["shareholder"];

// The three pickers whose choices are party ids. Each value names the party list
// the picker offers. The schema gives them no choicesFromQuestion (it cannot read
// a paneldynamic), so the browser fills them as the client types and the server
// fills them again for the stored schema and the PDF.
const PICKER_QUESTIONS = {
  holder_ref: "parties",
  primary_entity_id: "entities",
  ceo_equivalent_person_id: "people",
};

// Answers that steer the derivation and are not the party's own data.
const PERSON_CONTROL_FIELDS = ["person_id", "is_client", "primary_entity_id"];
const ENTITY_CONTROL_FIELDS = ["entity_id", "role_holders"];

// Role-row dispositions. Every row lands in exactly one.
const ROW_RELATIONSHIP = "relationship_candidate";
const ROW_CONTACT_PARENT = "contact_parent";
const ROW_UNSYNCED_ROLE = "unsynced_role";
const ROW_ERRORED = "errored";

// Party outcomes. Every party gets exactly one (R12).
const OUTCOME_CLIENT = "client";
const OUTCOME_CONTACT = "contact";
const OUTCOME_EXCLUDED = "excluded";
const OUTCOME_ERROR = "error";

const ENTITY_TYPE_VALUES = ENTITY_TYPES.map((t) => t.value);
const ROLE_VALUES = ROLES.map((r) => r.value);

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// Exported too: the client v2 modules use the same two shape guards.
const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const asArray = (value) => (Array.isArray(value) ? value : []);

// The answer blob is unauthenticated and not shape-checked, so an answer can
// arrive as an object or an array. Non-scalars become null rather than being
// stored verbatim (helper/clientOnboarding.js scalarAnswer).
function scalarAnswer(value) {
  if (value == null || typeof value === "object") return null;
  return String(value);
}

function trimmedString(value) {
  const scalar = scalarAnswer(value);
  return scalar === null ? "" : scalar.trim();
}

// A yes/no answer with three states. ABSENT IS NOT FALSE: writing
// `is_client || "no"` would invent an answer the client never gave, so an absent
// toggle stays null and is reported as unanswered.
function tristate(value) {
  if (value === true) return "yes";
  if (value === false) return "no";
  const scalar = trimmedString(value).toLowerCase();
  if (scalar === "yes" || scalar === "true") return "yes";
  if (scalar === "no" || scalar === "false") return "no";
  return null;
}

function numberOrNull(value) {
  const scalar = scalarAnswer(value);
  if (scalar === null || scalar.trim() === "") return null;
  const n = Number(scalar);
  return Number.isFinite(n) ? n : null;
}

function scalarData(answers, controlFields) {
  const data = {};
  for (const [key, value] of Object.entries(answers)) {
    if (controlFields.includes(key)) continue;
    const scalar = scalarAnswer(value);
    if (scalar !== null) data[key] = scalar;
  }
  return data;
}

// Array membership, never a map lookup: "constructor" is not in the list.
const isEntityType = (value) => ENTITY_TYPE_VALUES.includes(value);
const isRole = (value) => ROLE_VALUES.includes(value);

const unique = (values) => values.filter((value, i) => values.indexOf(value) === i);

// The one place a party's display name comes from. A stored row's own name wins,
// because staff can rename it. For an entity, the name comes from its type's own
// field and never from a scan for "*_name": clearInvisibleValues "none" leaves a
// stale trust_name next to company_name when the client changes the type.
function partyDisplayName(party) {
  if (!party) return "";
  if (party.name) return String(party.name).trim();
  if (party.kind === "person") {
    return [party.firstName, party.lastName].filter(Boolean).join(" ").trim();
  }
  const field = hasOwn(ENTITY_NAME_FIELD, party.entityType)
    ? ENTITY_NAME_FIELD[party.entityType]
    : null;
  return field ? String((party.data || {})[field] || "").trim() : "";
}

function roleText(role) {
  const found = ROLES.find((r) => r.value === role);
  return found ? found.text.toLowerCase() : String(role || "role").replace(/_/g, " ");
}

/*
 * parseSubmission()
 * SurveyJS answers -> { group, people[], entities[], rows[] }.
 *
 * Role rows are lifted out of their entity panels into one flat list, each with
 * a global `index`, so the accounting in classifyParties can be stated over rows.
 *
 * Hidden answers survive to here (the form sets clearInvisibleValues "none" so
 * the hidden ids survive), so an answer that only counts in one state is read
 * only in that state: role_other only for "other", the ownership % only for an
 * ownership role, the CEO/SMO pick only when the beneficial owners were not all
 * identified.
 */
function parseSubmission(submission) {
  const raw = isPlainObject(submission) ? submission : {};
  const beneficialOwnersIdentified = tristate(raw.beneficial_owners_identified);
  const picksCeo = beneficialOwnersIdentified === "no";

  const group = {
    name: trimmedString(raw.group_name),
    primaryContactName: trimmedString(raw.primary_contact_name),
    primaryContactEmail: trimmedString(raw.primary_contact_email),
    beneficialOwnersIdentified,
    ceoPersonId: picksCeo ? trimmedString(raw.ceo_equivalent_person_id) || null : null,
    ceoRole: picksCeo ? trimmedString(raw.ceo_equivalent_role) : "",
  };

  const people = asArray(raw.people).map((p, index) => {
    const answers = isPlainObject(p) ? p : {};
    return {
      kind: "person",
      index,
      id: trimmedString(answers.person_id),
      firstName: trimmedString(answers.firstName),
      lastName: trimmedString(answers.lastName),
      email: trimmedString(answers.email),
      isClient: tristate(answers.is_client),
      primaryEntityId: trimmedString(answers.primary_entity_id) || null,
      excluded: false,
      data: scalarData(answers, PERSON_CONTROL_FIELDS),
    };
  });

  const rows = [];
  const entities = asArray(raw.entities).map((e, index) => {
    const answers = isPlainObject(e) ? e : {};
    const entityId = trimmedString(answers.entity_id);

    asArray(answers.role_holders).forEach((h, rowIndex) => {
      const row = isPlainObject(h) ? h : {};
      const role = trimmedString(row.role);
      rows.push({
        index: rows.length,
        entityId,
        rowIndex,
        source: `entities[${index}].role_holders[${rowIndex}]`,
        holderRef: trimmedString(row.holder_ref),
        role,
        roleOther: role === "other" ? trimmedString(row.role_other) : "",
        ownershipPct: OWNERSHIP_ROLES.includes(role) ? numberOrNull(row.ownership_pct) : null,
      });
    });

    return {
      kind: "entity",
      index,
      id: entityId,
      entityType: trimmedString(answers.entity_type),
      excluded: false,
      data: scalarData(answers, ENTITY_CONTROL_FIELDS),
    };
  });

  return { group, people, entities, rows };
}

/*
 * classifyParties()
 * Parsed submission -> one outcome per party and one list per role row.
 *
 * The id namespace is the caller's: browser ids (p-/e-) on the form and at the
 * public endpoint, row UUIDs when the server plans or reclassifies stored rows.
 * People and entities share one namespace, so an edge key built from bare ids
 * cannot collide across kinds. A party with `excluded: true` was excluded by
 * staff.
 *
 * Parties are classified BEFORE edges are resolved, so a contact person can never
 * become a relationship endpoint: the property holds by construction, not by a
 * filter that could rot (helper/clientOnboarding.js resolveClientGroupRelationshipEdges).
 *
 * Errors carry { code, level, partyId, partyName, message }. `level` says what
 * the error is about: "submission" (ids), "party" (the party's own outcome),
 * "role" (one role row, reported on the entity panel it sits in) or "group".
 */
function classifyParties(parsed) {
  const input = parsed || {};
  const people = asArray(input.people);
  const entities = asArray(input.entities);
  const rows = asArray(input.rows);
  const group = input.group || {};

  const errors = [];
  const warnings = [];
  const outcomes = [];
  const clients = [];
  const contacts = [];
  const excluded = [];
  const unsyncedRoles = [];

  // --- 1. One id namespace -------------------------------------------------
  const byId = new Map();
  for (const party of [...people, ...entities]) {
    if (!party.id) {
      errors.push({
        code: "blank_party_id",
        level: "submission",
        partyId: null,
        partyName: partyDisplayName(party),
        message: `A ${party.kind} was submitted with no id and cannot be referenced.`,
      });
      continue;
    }
    if (byId.has(party.id)) {
      errors.push({
        code: "duplicate_party_id",
        level: "submission",
        partyId: party.id,
        partyName: partyDisplayName(party),
        message: `The id "${party.id}" is used by more than one party. Ids must be unique across people and entities.`,
      });
      continue;
    }
    byId.set(party.id, party);
  }

  const nameOf = (id) => partyDisplayName(byId.get(id)) || id || "";

  // --- 2. Every role row --------------------------------------------------
  const rowOutcomes = rows.map((row) => {
    const base = { ...row, disposition: null, reason: null };
    const entity = byId.get(row.entityId);

    // The row's own end must be an entity. A blank or duplicate entity id has
    // already been reported; its rows go down with it.
    if (!entity || entity.kind !== "entity") {
      return { ...base, disposition: ROW_ERRORED, reason: "unknown_entity" };
    }

    const entityName = nameOf(row.entityId);
    const fail = (code, message) => {
      errors.push({
        code,
        level: "role",
        partyId: row.entityId,
        partyName: entityName,
        holderId: row.holderRef || null,
        source: row.source,
        message,
      });
      return { ...base, disposition: ROW_ERRORED, reason: code };
    };

    if (!row.holderRef) {
      return fail("blank_endpoint", `${entityName}: a role has no person or entity selected.`);
    }
    const holder = byId.get(row.holderRef);
    if (!holder) {
      return fail(
        "unknown_entity",
        `${entityName}: a role points at a person or entity that is no longer in the form. Select it again.`
      );
    }
    if (row.holderRef === row.entityId) {
      return fail("self_reference", `${entityName} cannot hold a role in itself.`);
    }
    const holderName = nameOf(row.holderRef);
    if (!row.role) {
      return fail("missing_role", `${entityName}: select the role ${holderName} holds.`);
    }
    if (!isRole(row.role)) {
      return fail("unknown_role", `${entityName}: "${row.role}" is not a role this form offers.`);
    }
    if (OWNERSHIP_REQUIRED_ROLES.includes(row.role) && row.ownershipPct === null) {
      return fail(
        "missing_ownership_pct",
        `${entityName}: enter the ownership % for ${holderName}'s ${roleText(row.role)} role.`
      );
    }
    if (row.ownershipPct !== null && (row.ownershipPct < 0 || row.ownershipPct > 100)) {
      return fail(
        "invalid_ownership_pct",
        `${entityName}: the ownership % for ${holderName} must be between 0 and 100.`
      );
    }

    return { ...base, holder, entity };
  });

  const resolvedRows = rowOutcomes.filter((r) => r.disposition === null);
  const rowsByHolder = new Map();
  for (const row of resolvedRows) {
    if (!rowsByHolder.has(row.holderRef)) rowsByHolder.set(row.holderRef, []);
    rowsByHolder.get(row.holderRef).push(row);
  }

  // --- 3. The CEO/SMO pick (KTD22) ----------------------------------------
  let ceoPartyId = null;
  if (group.beneficialOwnersIdentified === "no") {
    const ceo = group.ceoPersonId ? byId.get(group.ceoPersonId) : null;
    if (!group.ceoPersonId) {
      errors.push({
        code: "ceo_missing",
        level: "group",
        partyId: null,
        partyName: "",
        message:
          "Not all beneficial owners were identified, so select the CEO or senior managing official from the people list.",
      });
    } else if (!ceo || ceo.kind !== "person") {
      errors.push({
        code: "ceo_unknown_person",
        level: "group",
        partyId: group.ceoPersonId,
        partyName: "",
        message: "The CEO or senior managing official is no longer in the people list. Select them again.",
      });
    } else {
      ceoPartyId = ceo.id;
      if (!ceo.email) {
        errors.push({
          code: "ceo_without_email",
          level: "group",
          partyId: ceo.id,
          partyName: nameOf(ceo.id),
          message: `${nameOf(ceo.id)} is the CEO or senior managing official and needs an email address for their identity check.`,
        });
      }
    }
  }

  const outcomeById = new Map();
  const record = (party, outcome, reason = null) => {
    outcomeById.set(party.id, outcome);
    outcomes.push({
      partyId: party.id,
      kind: party.kind,
      party,
      displayName: nameOf(party.id),
      outcome,
      reason,
    });
  };
  const exclude = (party, reason) => {
    excluded.push({
      partyId: party.id,
      kind: party.kind,
      party,
      displayName: nameOf(party.id),
      reason,
      rows: rowsByHolder.get(party.id) || [],
    });
    record(party, OUTCOME_EXCLUDED, reason);
  };

  // --- 4. Entities --------------------------------------------------------
  for (const entity of entities) {
    if (byId.get(entity.id) !== entity) continue;
    const name = nameOf(entity.id);

    if (!isEntityType(entity.entityType)) {
      const what = entity.entityType
        ? `: "${entity.entityType}" is not an entity type this form offers.`
        : " has no entity type.";
      const hint =
        entity.entityType === "individual"
          ? " Add a sole trader as a person and onboard them as a client."
          : "";
      errors.push({
        code: "unknown_entity_type",
        level: "party",
        partyId: entity.id,
        partyName: name,
        message: `${name || "An entity"}${what}${hint}`,
      });
      record(entity, OUTCOME_ERROR, "unknown_entity_type");
      continue;
    }
    if (entity.excluded) {
      exclude(entity, "staff_excluded");
      continue;
    }
    clients.push({ partyId: entity.id, kind: "entity", party: entity });
    record(entity, OUTCOME_CLIENT);
  }

  // --- 5. People ----------------------------------------------------------
  for (const person of people) {
    if (byId.get(person.id) !== person) continue;

    const held = rowsByHolder.get(person.id) || [];
    const name = nameOf(person.id);
    const fail = (code, message, extra = {}) => {
      errors.push({ code, level: "party", partyId: person.id, partyName: name, message, ...extra });
      record(person, OUTCOME_ERROR, code);
    };

    if (person.isClient === null) {
      fail("unanswered_is_client", `${name}: answer "Onboard this person as a client?".`);
      continue;
    }

    if (person.isClient === "yes") {
      if (person.excluded) {
        exclude(person, "staff_excluded");
        continue;
      }
      clients.push({ partyId: person.id, kind: "person", party: person });
      record(person, OUTCOME_CLIENT);
      if (!person.email) {
        warnings.push({
          code: "client_without_email",
          partyId: person.id,
          message: `${name} is onboarded as a client but has no email address, so no identity check can be sent to them.`,
        });
      }
      continue;
    }

    // The answer is No from here.
    if (held.length === 0) {
      if (person.id === ceoPartyId) {
        exclude(person, "ceo_only");
        continue;
      }
      // Name the real cause: "holds no role" misleads when every role entered
      // for the person has an error.
      const attempted = rowOutcomes.filter(
        (r) => r.holderRef === person.id && r.disposition === ROW_ERRORED
      );
      const because = attempted.length
        ? ` Every role entered for them has an error (${unique(attempted.map((r) => r.reason)).join(", ")}).`
        : "";
      fail(
        "party_produces_nothing",
        `${name} is not onboarded as a client and holds no role in any entity, so nothing would be created for them.${because} Either onboard them as a client, or remove them.`,
        { resolutions: ["mark_as_client", "delete_person"] }
      );
      continue;
    }

    if (person.excluded) {
      exclude(person, "staff_excluded");
      continue;
    }
    if (!person.email) {
      exclude(person, "no_email");
      continue;
    }

    // One parent: metadata.parent_entity_id is a scalar and an XPM contact
    // belongs to exactly one client. Only an entity that becomes a client can be
    // the parent, so an excluded entity hands its contacts to the next one.
    const candidates = held.filter((r) => outcomeById.get(r.entityId) === OUTCOME_CLIENT);
    if (candidates.length === 0) {
      exclude(person, "parent_excluded");
      continue;
    }

    const pick = person.primaryEntityId;
    const pickHeld = Boolean(pick) && held.some((r) => r.entityId === pick);
    const pickUsable = pickHeld && candidates.some((r) => r.entityId === pick);
    const chosen = pickUsable ? pick : candidates[0].entityId;

    if (pick && !pickHeld) {
      warnings.push({
        code: "primary_entity_not_held",
        partyId: person.id,
        message: `${name} picked a parent entity they hold no role in, so ${nameOf(chosen)} was used instead.`,
      });
    } else if (pickHeld && !pickUsable) {
      warnings.push({
        code: "parent_entity_excluded",
        partyId: person.id,
        message: `${name}'s parent entity ${nameOf(pick)} is excluded from XPM, so they move to ${nameOf(chosen)}.`,
      });
    }

    const parentRow = candidates.find((r) => r.entityId === chosen);
    contacts.push({
      partyId: person.id,
      kind: "person",
      party: person,
      displayName: name,
      parentEntityId: chosen,
      parentDefaulted: chosen !== pick,
      parentRow,
      otherRows: held.filter((r) => r !== parentRow),
    });
    record(person, OUTCOME_CONTACT);
  }

  // --- 6. Every resolved row gets its list ----------------------------------
  const contactsById = new Map(contacts.map((c) => [c.partyId, c]));
  const unsynced = (row, reason) => {
    row.disposition = ROW_UNSYNCED_ROLE;
    row.reason = reason;
    const holderName = nameOf(row.holderRef);
    const entityName = nameOf(row.entityId);
    const role = roleText(row.role);
    unsyncedRoles.push({
      partyId: row.holderRef,
      displayName: holderName,
      entityId: row.entityId,
      entityName,
      role: row.role,
      roleOther: row.roleOther || null,
      reason,
      row,
      message:
        reason === "contact_secondary_role"
          ? `${holderName} is a contact under one client, so their ${role} role at ${entityName} is recorded here and not sent to XPM. Onboarding them as a client would send it as a relationship.`
          : `${holderName}'s ${role} role at ${entityName} is recorded here and not sent to XPM, because ${
              reason === "entity_excluded" ? entityName : holderName
            } is excluded from XPM.`,
    });
  };

  for (const row of resolvedRows) {
    const holderOutcome = outcomeById.get(row.holderRef);
    const entityOutcome = outcomeById.get(row.entityId);
    if (entityOutcome === OUTCOME_EXCLUDED && holderOutcome !== OUTCOME_ERROR) {
      unsynced(row, "entity_excluded");
    } else if (holderOutcome === OUTCOME_EXCLUDED && entityOutcome !== OUTCOME_ERROR) {
      unsynced(row, "holder_excluded");
    } else if (holderOutcome === OUTCOME_CLIENT && entityOutcome === OUTCOME_CLIENT) {
      row.disposition = ROW_RELATIONSHIP;
    } else if (holderOutcome === OUTCOME_CONTACT && entityOutcome === OUTCOME_CLIENT) {
      if (row === contactsById.get(row.holderRef).parentRow) {
        row.disposition = ROW_CONTACT_PARENT;
      } else {
        unsynced(row, "contact_secondary_role");
      }
    } else {
      // One end errored. The submission is refused anyway; the row is listed
      // with its party rather than guessed at.
      row.disposition = ROW_ERRORED;
      row.reason = "party_errored";
    }
  }

  // --- 7. Accounting identities -------------------------------------------
  // Stated over OUTPUT, so each can actually fail: a party counted in two
  // buckets, a party in none, or a row in no rendered list.
  const partyBuckets = [
    ...clients.map((c) => c.partyId),
    ...contacts.map((c) => c.partyId),
    ...excluded.map((x) => x.partyId),
    ...errors.filter((e) => e.level === "party").map((e) => e.partyId),
  ];
  const partyCounts = new Map();
  for (const id of partyBuckets) partyCounts.set(id, (partyCounts.get(id) || 0) + 1);
  const unaccountedParties = [...byId.keys()].filter((id) => !partyCounts.has(id));
  const doubleAccountedParties = [...partyCounts.entries()]
    .filter(([, n]) => n > 1)
    .map(([id]) => id);

  const rendered = new Set([
    ...rowOutcomes.filter(
      (r) => r.disposition === ROW_ERRORED || r.disposition === ROW_RELATIONSHIP
    ),
    ...contacts.map((c) => c.parentRow),
    ...unsyncedRoles.map((u) => u.row),
  ]);
  const unaccountedRows = rowOutcomes.filter((r) => !rendered.has(r)).map((r) => r.index);

  return {
    group,
    outcomes,
    clients,
    contacts,
    excluded,
    unsyncedRoles,
    errors,
    warnings,
    rowOutcomes,
    relationshipRows: rowOutcomes.filter((r) => r.disposition === ROW_RELATIONSHIP),
    ceoPartyId,
    accounting: {
      partiesTotal: byId.size,
      unaccountedParties,
      doubleAccountedParties,
      rowsTotal: rowOutcomes.length,
      unaccountedRows,
      balanced:
        unaccountedParties.length === 0 &&
        doubleAccountedParties.length === 0 &&
        unaccountedRows.length === 0,
    },
  };
}

// The browser's pre-POST gate and the public endpoint's 400: the errors the
// client must fix, each with the party name, and the warnings.
function checkSubmission(submission) {
  const { errors, warnings } = classifyParties(parseSubmission(submission));
  return { errors, warnings };
}

module.exports = {
  ENTITY_TYPES,
  ENTITY_TYPE_VALUES,
  ENTITY_NAME_FIELD,
  ROLES,
  ROLE_VALUES,
  OWNERSHIP_ROLES,
  OWNERSHIP_REQUIRED_ROLES,
  PICKER_QUESTIONS,
  ROW_RELATIONSHIP,
  ROW_CONTACT_PARENT,
  ROW_UNSYNCED_ROLE,
  ROW_ERRORED,
  OUTCOME_CLIENT,
  OUTCOME_CONTACT,
  OUTCOME_EXCLUDED,
  OUTCOME_ERROR,
  isEntityType,
  isRole,
  isPlainObject,
  asArray,
  partyDisplayName,
  scalarAnswer,
  trimmedString,
  tristate,
  parseSubmission,
  classifyParties,
  checkSubmission,
};
